import React from 'react';
import { AutoAwesome as AssistantIcon, Person as PersonIcon } from '@mui/icons-material';
import { Avatar as MuiAvatar, useTheme } from '@mui/material';

import type { AvatarProps } from './Avatar.types';

const AVATAR_SIZES = {
  sm: 24,
  md: 32,
  lg: 48,
};

const ICON_SIZES = {
  sm: 14,
  md: 18,
  lg: 28,
};

/**
 * Avatar component for chat messages with support for user and assistant roles
 * Automatically applies theme-aware styling based on role
 *
 * @example
 * ```tsx
 * <Avatar role="assistant" />
 * <Avatar role="user" size="lg" />
 * <Avatar role="assistant" icon={<CustomIcon />} />
 * ```
 */
export const Avatar: React.FC<AvatarProps> = ({
  role,
  icon,
  size = 'md',
  backgroundColor,
  gradient,
  className,
}) => {
  const theme = useTheme();

  // Determine avatar size
  const avatarSize = AVATAR_SIZES[size];
  const iconSize = ICON_SIZES[size];

  // Default icons based on role
  const defaultIcon =
    role === 'assistant' ? (
      <AssistantIcon sx={{ fontSize: iconSize }} />
    ) : (
      <PersonIcon sx={{ fontSize: iconSize }} />
    );

  const displayIcon = icon || defaultIcon;

  // Determine background styling
  const getBackgroundStyle = () => {
    // If gradient is provided, use it
    if (gradient) {
      return {
        bgcolor: 'transparent',
        background: `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`,
      };
    }

    // If custom backgroundColor is provided, use it
    if (backgroundColor) {
      return { bgcolor: backgroundColor };
    }

    // Default styling based on role
    if (role === 'assistant') {
      return {
        bgcolor: 'transparent',
        background: theme.palette.chat.gradients.ai,
      };
    }

    // User role default
    return {
      bgcolor: theme.palette.chat.colors.aiDark,
    };
  };

  return (
    <MuiAvatar
      className={className}
      sx={{
        width: avatarSize,
        height: avatarSize,
        ...getBackgroundStyle(),
      }}
    >
      {displayIcon}
    </MuiAvatar>
  );
};
